import React, { useMemo } from 'react'
import { Icon } from '@iconify/react'
import { BotIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'

export type IntegrationPlatform =
  | 'meta'
  | 'google'
  | 'admob'
  | 'ga4'
  | 'gsc'
  | 'gtm'
  | 'posthog'
  | 'tiktok'
  | 'linkedin'
  | 'reddit'
  | 'x_ads'
  | 'chatbot'
  | 'notion'
  | 'gmail'
  | 'googledocs'
  | 'googlesheets'
  | 'googledrive'
  | 'googlecalendar'
  | 'fireflies'
  | 'monday'
  | 'chatgpt'
  | 'stripe'
  | 'appsflyer'
  | 'adjust'
  | 'topon'
  | 'cloudx'
  | 'sprout'
  | 'shopify'
  | 'shoplazza'
  | 'shopline'
  | 'bigquery'
  | 'toast'
  | 'doordash'
  | 'ubereats'
  | 'applovin'
  | 'applovin_max'
  | 'thinkingdata'
  | 'sensortower'
export type CredentialPlatform =
  | 'meta'
  | 'google'
  | 'admob'
  | 'ga4'
  | 'gsc'
  | 'gtm'
  | 'posthog'
  | 'tiktok'
  | 'linkedin'
  | 'reddit'
  | 'x_ads'
  | 'notion'
  | 'gmail'
  | 'googledocs'
  | 'googlesheets'
  | 'googledrive'
  | 'googlecalendar'
  | 'fireflies'
  | 'monday'
  | 'composio_managed'
  | 'stripe'
  | 'appsflyer'
  | 'adjust'
  | 'topon'
  | 'cloudx'
  | 'sprout'
  | 'shopify'
  | 'bigquery'
  | 'toast'
  | 'doordash'
  | 'ubereats'
  | 'applovin'
  | 'applovin_max'
  | 'thinkingdata'
  | 'sensortower'

/** Which section of Settings → Integrations a card belongs to.
 *
 * ONE taxonomy, shared with the app catalog. The first three are the kind of
 * asset the user is handing over — "where does my spend / traffic / revenue
 * live?" — and no catalog app ever lands in them. The rest are the catalog's
 * own buckets (see `_lib/connection-buckets.ts`), and a first-party card is
 * free to declare one: Notion's card belongs next to the other doc apps, not
 * in a separate "Workspace" pile that says the same thing twice.
 *
 * That `workspace` section is what this replaced. It held the Composio-backed
 * long tail (Notion, Gmail, Drive, monday, …) while the catalog sorted its own
 * copies of those same apps into `docs` / `comms` / `work`, so the nav carried
 * two names for one idea and neither list was complete.
 */
export type IntegrationSection =
  | 'channels'
  | 'analytics'
  | 'revenue'
  | 'ai'
  | 'dev'
  | 'crm'
  | 'marketing'
  | 'docs'
  | 'comms'
  | 'commerce'
  | 'work'
  | 'other'

export interface Integration {
  id: IntegrationPlatform
  name: string
  icon: React.ReactNode
  /**
   * English fallback copy. Cards render the locale-resolved text instead —
   * see `useLocalizedIntegrations`, which swaps this for
   * `SettingsPages.integrations.catalog.descriptions.<id>`.
   */
  description: string
  supportsOAuth: boolean
  enabled: boolean
  /** Closed set so the card can translate it; the label is not raw copy. */
  badge?: 'new'
  /** Section this card renders under in Settings → Integrations. Every card
   * declares one — an omission would silently drop it out of the grid. */
  section: IntegrationSection
  /** Presence opts the integration into the onboarding "Connect Your Tools"
   * step (single source of truth — the page renders exactly these, no local
   * whitelist). `section` picks the band, `rank` orders within it. New
   * integrations stay out of onboarding unless explicitly opted in here. */
  onboarding?: { section: 'ads' | 'data' | 'other'; rank: number }
}

export const COMPOSIO_MANAGED_PLATFORM = 'composio_managed' as const

/** Composio toolkit slug behind each UI card, keyed by CARD id.
 *
 * Card id and toolkit slug are two different namespaces that happen to
 * coincide for every long-tail card. The code used to encode that coincidence
 * as an identity (`COMPOSIO_PLATFORMS.has(cardId)` then reuse `cardId` as the
 * toolkit) — BigQuery breaks it: its card is `bigquery`, its toolkit is
 * `googlebigquery`. Everything that used to branch on set membership now
 * resolves through this table instead, so the two namespaces stay separable.
 */
export const COMPOSIO_TOOLKIT_BY_PLATFORM = {
  notion: 'notion',
  gmail: 'gmail',
  googledocs: 'googledocs',
  googlesheets: 'googlesheets',
  googledrive: 'googledrive',
  googlecalendar: 'googlecalendar',
  fireflies: 'fireflies',
  monday: 'monday',
  bigquery: 'googlebigquery',
  // Composio ships a second, SOCIAL Reddit toolkit whose slug is `reddit` —
  // the card id. The mapping is the only thing keeping the ads card off it.
  reddit: 'reddit_ads',
} as const satisfies Partial<Record<IntegrationPlatform, string>>

/** Cards that ALSO offer a first-party connect mode whose credentials are
 * stored under the card's own `platform` value instead of `composio_managed`.
 *
 * BigQuery is the first: a pasted service-account key lands as
 * `platform='bigquery', source='service_account'`, while the managed OAuth
 * lands as `platform='composio_managed'` with toolkit `googlebigquery`. Both
 * belong to the one card, so card ownership stopped being a mutually exclusive
 * branch. Every other entry in the table above is Composio-only and keeps
 * exactly its previous behavior.
 */
const FIRST_PARTY_COMPOSIO_PLATFORMS = new Set<IntegrationPlatform>([
  'bigquery',
])

/** Cards whose ONLY connect mode is Composio managed OAuth, but whose
 * credential is stored under the card's own first-party `platform`.
 *
 * Reddit Ads is the first. Composio is just the transport (consent, token
 * custody, proxy); the connection lands as `platform='reddit'` because its
 * grantable rows are `ad_accounts`, and one connection spans many of them. So
 * it is neither shape above: not a catalog card (it belongs in the channels
 * grid and must not match `composio_managed` rows only), and not dual-mode
 * (one connect mode, one resource kind — granting an ad account grants that
 * ad account and nothing else). */
const COMPOSIO_TRANSPORTED_FIRST_PARTY_PLATFORMS = new Set<IntegrationPlatform>(
  ['reddit'],
)

/** True when the card connects through Composio but stores its credential
 * under its own first-party platform (see
 * {@link COMPOSIO_TRANSPORTED_FIRST_PARTY_PLATFORMS}). */
export function isComposioTransportedFirstPartyPlatform(
  id: IntegrationPlatform | string,
): boolean {
  return COMPOSIO_TRANSPORTED_FIRST_PARTY_PLATFORMS.has(
    id as IntegrationPlatform,
  )
}

/** Platforms whose backend refuses an in-place reconnect target.
 *
 * Their authorize hop rejects `reconnect_credential_id` (Reddit and X Ads
 * both refuse it rather than repair the wrong credential), so passing one
 * makes Reconnect fail every time. Reconnect runs a FRESH connect instead:
 * the callback matches the authorizing identity to its existing credential
 * (X: the same X user stages onto that credential; Reddit: already-imported
 * accounts show on the selection step, where Move re-binds them). */
const FRESH_CONNECT_RECONNECT_PLATFORMS = new Set<IntegrationPlatform>([
  'x_ads',
])

export function reconnectsAsFreshConnect(
  id: IntegrationPlatform | string,
): boolean {
  return (
    isComposioTransportedFirstPartyPlatform(id) ||
    FRESH_CONNECT_RECONNECT_PLATFORMS.has(id as IntegrationPlatform)
  )
}

/** True when the card owns BOTH connect modes at once — its own first-party
 * platform AND a Composio toolkit.
 *
 * This is the one property that decides whether a card's grantable rows can be
 * of MORE THAN ONE resource kind, and therefore whether a single card-level
 * noun ("project") is allowed to describe all of them. BigQuery's two legs
 * produce `bigquery_project` rows (one per GCP project — granting one grants
 * that project) and one `composio_connected_account` row (granting it grants
 * every project the Google identity can reach). A Composio-ONLY card is not
 * dual-mode: its rows are all `composio_connected_account`, so its card-level
 * noun stays correct and its presentation is untouched. */
export function isDualModeIntegrationPlatform(
  id: IntegrationPlatform | string,
): boolean {
  return FIRST_PARTY_COMPOSIO_PLATFORMS.has(id as IntegrationPlatform)
}

/** Composio toolkit slug this card connects through, or undefined when the
 * card has no Composio leg at all (meta, google, ga4, …). */
export function composioToolkitForPlatform(
  id: IntegrationPlatform | string,
): string | undefined {
  return (COMPOSIO_TOOLKIT_BY_PLATFORM as Record<string, string>)[id]
}

const PLATFORM_BY_COMPOSIO_TOOLKIT: Record<string, IntegrationPlatform> =
  Object.fromEntries(
    Object.entries(COMPOSIO_TOOLKIT_BY_PLATFORM).map(([platform, toolkit]) => [
      toolkit,
      platform as IntegrationPlatform,
    ]),
  )

/** Reverse of {@link composioToolkitForPlatform}: which card owns a toolkit
 * slug. Returns undefined for long-tail catalog toolkits that have no card of
 * their own — including one whose slug collides with a NATIVE platform (a
 * `stripe` catalog toolkit must never pick up the native Stripe card). */
export function integrationPlatformForComposioToolkit(
  toolkit: string | null | undefined,
): IntegrationPlatform | undefined {
  if (!toolkit) return undefined
  return PLATFORM_BY_COMPOSIO_TOOLKIT[toolkit]
}

/** True when the card is served from the Composio app catalog rather than the
 * first-party platform grid — i.e. it is Composio-backed AND has no
 * first-party connect mode of its own. This is what the Settings grid filter
 * and the OAuth-only classification actually mean; membership in
 * {@link COMPOSIO_TOOLKIT_BY_PLATFORM} alone no longer implies either. */
export function isComposioCatalogPlatform(
  id: IntegrationPlatform | string,
): boolean {
  return (
    composioToolkitForPlatform(id) !== undefined &&
    !isDualModeIntegrationPlatform(id) &&
    !isComposioTransportedFirstPartyPlatform(id)
  )
}

/** Minimal credential shape needed to decide which card owns a credential. */
export type PlatformCredentialLike = {
  platform: string
  source?: string | null
  composio_toolkit_slug?: string | null
}

/** Does this credential belong to this card?
 *
 * Four cases, and the first two are byte-for-byte the previous behavior:
 * - Composio-only card → only `(composio_managed, source=composio, slug)`.
 * - Card with no Composio leg → only `credential.platform === cardId`.
 * - Card with BOTH (BigQuery) → either one. Its two connect modes live in two
 *   different storage platforms and must resolve to the same card.
 * - Composio-transported first-party card (Reddit Ads) → ONLY
 *   `platform === cardId`. Its one connect mode stores there. A
 *   `composio_managed` row with its toolkit slug is deliberately NOT claimed:
 *   such a row can only predate the card, and it carries a connection-wide
 *   grant rather than per-ad-account grants, so presenting it as this card's
 *   connection would misstate what a Brand can reach.
 */
export function credentialMatchesIntegrationPlatform(
  credential: PlatformCredentialLike,
  id: IntegrationPlatform | string,
): boolean {
  if (isComposioTransportedFirstPartyPlatform(id)) {
    return credential.platform === id
  }
  const toolkit = composioToolkitForPlatform(id)
  if (toolkit !== undefined) {
    if (
      credential.platform === COMPOSIO_MANAGED_PLATFORM &&
      credential.source === 'composio' &&
      credential.composio_toolkit_slug === toolkit
    ) {
      return true
    }
    // A Composio-only card has no storage platform of its own, so matching on
    // the card id here would widen the match for every catalog card at once.
    if (!isDualModeIntegrationPlatform(id)) {
      return false
    }
  }
  return credential.platform === id
}

/** OAuth-only platforms (no token/service account alternative). */
export const isOAuthOnlyPlatform = (id: IntegrationPlatform): boolean =>
  id === 'admob' ||
  id === 'ga4' ||
  id === 'gsc' ||
  id === 'gtm' ||
  id === 'posthog' ||
  id === 'tiktok' ||
  id === 'linkedin' ||
  // Uber Eats issues merchant tokens only through its hosted OAuth consent —
  // there is no pasted-token alternative, so Connect goes straight to the
  // authorize redirect (GA4 model).
  id === 'ubereats' ||
  // X Ads has no pasted-token mode either: its authorize URL can only be minted
  // by a signed server-side request-token call. Without this entry the flipped
  // card would fall through to the connect dialog, which has no X panel — a
  // Connect button that does nothing.
  id === 'x_ads' ||
  // Reddit Ads connects only through Composio's hosted consent; there is no
  // pasted-token mode to offer.
  isComposioTransportedFirstPartyPlatform(id) ||
  isComposioCatalogPlatform(id)

/** Token-only platforms: connected solely via a pasted token/key, with NO OAuth
 * option. The connect dialog opens straight to the setup form (no auth-method
 * chooser). AppsFlyer / Adjust MCP have no programmatic OAuth for our backend
 * forwarder, and Sprout Social's Public API only issues personal API tokens —
 * unlike Stripe, which also offers hosted OAuth, so its dialog legitimately
 * shows both. */
/** Cards whose ONLY connect mode is a pasted-credentials form.
 *
 * Routing depends on this: a platform that is neither OAuth-only nor
 * token-only opens the generic dialog, which defaults to the OAuth panel — so
 * omitting a paste-only platform here strands its form behind an OAuth screen
 * it can never satisfy. Toast is token-only in this sense even though its form
 * takes three fields rather than one: there is no Toast OAuth flow at all.
 * DoorDash is the limit case — its form carries no secret at all (business
 * IDs only; the platform credential is Soku-side) — but the routing property
 * is the same: the setup form is its ONLY connect mode.
 */
export const isTokenOnlyPlatform = (id: IntegrationPlatform): boolean =>
  id === 'appsflyer' ||
  id === 'adjust' ||
  id === 'topon' ||
  // CloudX: bearer API key created under Settings > API Keys; no OAuth.
  id === 'cloudx' ||
  id === 'sprout' ||
  id === 'toast' ||
  id === 'doordash' ||
  id === 'applovin' ||
  id === 'applovin_max' ||
  // ThinkingData: customer-typed service address + project key + project ids;
  // no OAuth exists (the key is generated on the customer's own server).
  id === 'thinkingdata' ||
  // Sensor Tower: one API token from the customer's account page; no OAuth.
  id === 'sensortower' ||
  // Shopify is here for NEW connects only. Its OAuth flow exists and still
  // repairs the rows that used it (see FORM_RECONNECT_SOURCE_BY_PLATFORM and
  // the oauth-repair override in connect-dialog-routing), but Soku's public app
  // is not App Store approved — offering that hop to a merchant today walks
  // them into an install they cannot complete. A merchant-owned custom app is
  // the connect mode that actually works, so it is the only one presented.
  id === 'shopify'

// ChatGPT Ads report accounts are staff-provisioned and white-labeled: the
// upstream advertiser name (e.g. the provider account owner) must never surface
// to customers. Display a neutral "Soku" label instead, everywhere the
// integrations UI would otherwise show the raw ad-account / advertiser name.
const CHATGPT_ADS_ADVERTISER_LABEL = 'Soku'

export function advertiserDisplayLabel(
  platformId: string,
  fallback: string,
): string {
  return platformId === 'chatgpt' || platformId === 'chatgpt_ads'
    ? CHATGPT_ADS_ADVERTISER_LABEL
    : fallback
}

export function brandAwareAdvertiserDisplayLabel(
  platformId: string,
  fallback: string,
  brandIds: Iterable<string>,
  brands: ReadonlyArray<{ id: string; name: string }>,
  unassignedLabel: string,
): string {
  const accountLabel = advertiserDisplayLabel(platformId, fallback)
  if (platformId !== 'chatgpt' && platformId !== 'chatgpt_ads') {
    return accountLabel
  }

  const selectedBrandIds = new Set(brandIds)
  const selectedBrandNames = brands
    .filter(brand => selectedBrandIds.has(brand.id))
    .toSorted(
      (left, right) =>
        left.name.localeCompare(right.name) || left.id.localeCompare(right.id),
    )
    .map(brand => brand.name)
  const brandLabel =
    selectedBrandNames.length > 0
      ? selectedBrandNames.join(' / ')
      : unassignedLabel
  return `${accountLabel} · ${brandLabel}`
}

export const platformEnabled: Record<IntegrationPlatform, boolean> = {
  meta: true,
  google: true,
  admob: true,
  ga4: true,
  gsc: true,
  // New connections stay hidden until Google approves the GTM OAuth scopes.
  gtm: false,
  posthog: true,
  tiktok: true,
  linkedin: true,
  chatbot: false,
  notion: true,
  gmail: true,
  googledocs: true,
  googlesheets: true,
  googledrive: true,
  googlecalendar: true,
  fireflies: false,
  monday: true,
  chatgpt: true,
  stripe: true,
  appsflyer: true,
  adjust: true,
  topon: true,
  cloudx: true,
  sprout: true,
  // Live via the merchant-owned custom app path (client-credentials BYOK),
  // which needs no App Store approval and no Soku-side public app. The OAuth
  // hop stays in the code for the rows that already use it, but is not offered
  // as a connect mode (see isTokenOnlyPlatform).
  shopify: true,
  // Shoplazza has no Soku-side connect entry — stores are installed by the
  // merchant from the Shoplazza App Store. The card is a standing one anyway:
  // orgs with a parked store manage its brand allocation here, and orgs
  // without one get an outbound CTA to Shoplazza (see the page-content's
  // externalInstallAction), which is the point of the integration today.
  shoplazza: true,
  // SHOPLINE mirrors Shoplazza: no Soku-side connect entry — stores are
  // installed by the merchant from the SHOPLINE App Store, and the card is
  // gated to orgs that actually parked a store.
  shopline: true,
  bigquery: true,
  toast: true,
  // AppLovin needs no Soku-side provisioning: the customer's own Report Key
  // is the whole credential, so the card is live from day one (unlike
  // DoorDash / Uber Eats, which wait on a platform credential or an app
  // approval that does not exist yet).
  applovin: true,
  applovin_max: true,
  // ThinkingData needs no Soku-side provisioning either: the customer's own
  // gateway address and project key are the whole credential.
  thinkingdata: true,
  // Sensor Tower: the customer's own API token is the whole credential.
  sensortower: true,
  // Hidden until DoorDash grants Reporting API access. The whole connect flow
  // is built and tested, but Soku's platform credential does not exist yet, so
  // every connect attempt would answer `platform_not_configured` — an honest
  // message, and still a dead end we should not put in front of customers.
  // `false` REMOVES the card (the grid keeps only `enabled` platforms; the
  // "Coming soon" teaser is a shopify-only exception), and `handleConnect`
  // guards on the same flag.
  //
  // FLIP TO `true` as the last step of the post-approval checklist in
  // docs/modules/integrations/operations.md — after the GSM triple is injected
  // AND the STORE_INFORMATION smoke test passes. Flipping it earlier just
  // restores the dead end; forgetting to flip it ships the integration
  // invisible.
  doordash: false,
  // Uber Eats stays off until Uber approves the Soku developer app, and
  // unlike Shopify it is not a "Coming soon" teaser either — it is absent
  // from the grid entirely (it is deliberately NOT in TEASER_PLATFORMS),
  // because showing a card for an unapproved partnership advertises something
  // that does not exist yet.
  //
  // The gate is also load-bearing, not cosmetic: the authorize hop is signed
  // with the platform-wide UBEREATS_CLIENT_ID / UBEREATS_CLIENT_SECRET pair,
  // which is unprovisioned until approval, so a Connect press would fail
  // closed with `oauth_not_configured` AND fire the FM20 P0 ops alert
  // (api/services/credential_service.py's `_alert_oauth_not_configured`) — a
  // guaranteed page, not an accident.
  //
  // Flipping this to `true` is the deploy-side half of enabling the connector;
  // the other half is provisioning the env pair in GSM. Nothing else needs to
  // change: `enabled: true` puts the card back in the grid on its own.
  ubereats: false,
  // Reddit Ads / X Ads ship dark behind the backend's REDDIT_ADS_ENABLED /
  // X_ADS_ENABLED, which also refuse the connect itself while off. Do NOT flip
  // these defaults: the integrations page overrides them per request from the
  // same runtime switches (lib/ad-platform-switches.ts), so a card shows
  // exactly where its switch is on. `false` hides it everywhere else (not a
  // teaser), including the marketing pages.
  reddit: false,
  x_ads: false,
}

/** Disabled cards that STAY in the Settings → Integrations grid as a
 * "Coming soon" teaser instead of disappearing.
 *
 * `enabled: false` normally means "not in the grid at all"; a platform listed
 * here renders with a disabled "Coming soon" CTA (platform-card's
 * `isComingSoon`) and `handleConnect` refuses it, so the connector reads as
 * pending rather than absent and no user can start a flow that cannot complete.
 *
 * Empty today. Shopify used to be the only member: it is now connectable
 * through the merchant's own custom app, so it is a live card rather than a
 * teaser. What stays "coming soon" for Shopify is narrower than a whole card —
 * only the OAuth hop, which is simply not offered as a connect mode (see
 * isTokenOnlyPlatform).
 *
 * Uber Eats is deliberately NOT here. Product decision: it must not appear in
 * the UI at all until Uber approves the Soku developer app — a visible
 * "Coming soon" card advertises a partnership that does not exist yet. It
 * stays fully dark (no card, no CTA) and becomes visible in the same change
 * that flips `platformEnabled.ubereats` to `true`; see the post-approval
 * runbook in docs/modules/integrations/operations.md.
 */
export const TEASER_PLATFORMS: ReadonlySet<IntegrationPlatform> =
  new Set<IntegrationPlatform>()

/** True when a card belongs in the native platform grid: connectable today,
 * or a deliberate "Coming soon" teaser. */
export const isPlatformGridVisible = (integration: Integration): boolean =>
  integration.enabled || TEASER_PLATFORMS.has(integration.id)

export type IntegrationRuntimeOverrides = Partial<
  Record<IntegrationPlatform, boolean>
>

export function isIntegrationEnabled(
  platform: IntegrationPlatform,
  overrides: IntegrationRuntimeOverrides = {},
): boolean {
  return overrides[platform] ?? platformEnabled[platform]
}

interface IconProps {
  size?: 'xs' | 'sm' | 'md' | 'lg'
}

const iconSizeMap = {
  // `xs` is a compact glyph for dense inline controls (e.g. the dashboard
  // account-platform segmented picker), where the logo sits next to
  // text-sized UI rather than inside a card.
  xs: 'size-4',
  // `sm` is the size used in the Settings → Integrations grid, where each
  // icon is centered inside a size-10 wrapper. Keep the inner glyph small
  // so logos with built-in viewBox padding (Google, Gmail, …) and logos
  // that fill their viewBox (Meta) all read at the same visual weight.
  sm: 'size-7',
  md: 'size-12',
  lg: 'size-16',
}

export const MetaIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  const gradientId = `metaGradient-${size}`

  return (
    <svg
      className={sizeClass}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#0081FB', stopOpacity: 1 }} />
          <stop
            offset="100%"
            style={{ stopColor: '#0064E0', stopOpacity: 1 }}
          />
        </linearGradient>
      </defs>
      <path
        d="M5,19.5c0-4.6,2.3-9.4,5-9.4c1.5,0,2.7,0.9,4.6,3.6c-1.8,2.8-2.9,4.5-2.9,4.5c-2.4,3.8-3.2,4.6-4.5,4.6 C5.9,22.9,5,21.7,5,19.5 M20.7,17.8L19,15c-0.4-0.7-0.9-1.4-1.3-2c1.5-2.3,2.7-3.5,4.2-3.5c3,0,5.4,4.5,5.4,10.1 c0,2.1-0.7,3.3-2.1,3.3S23.3,22,20.7,17.8 M16.4,11c-2.2-2.9-4.1-4-6.3-4C5.5,7,2,13.1,2,19.5c0,4,1.9,6.5,5.1,6.5 c2.3,0,3.9-1.1,6.9-6.3c0,0,1.2-2.2,2.1-3.7c0.3,0.5,0.6,1,0.9,1.6l1.4,2.4c2.7,4.6,4.2,6.1,6.9,6.1c3.1,0,4.8-2.6,4.8-6.7 C30,12.6,26.4,7,22.1,7C19.8,7,18,8.8,16.4,11"
        fill={`url(#${gradientId})`}
      />
    </svg>
  )
}

export const GoogleIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]

  return (
    <svg
      className={sizeClass}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
        fill="#FFC107"
      />
      <path
        d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
        fill="#FF3D00"
      />
      <path
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
        fill="#4CAF50"
      />
      <path
        d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
        fill="#1976D2"
      />
    </svg>
  )
}

export const GA4Icon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  return <Icon icon="logos:google-analytics" className={sizeClass} />
}

export const SearchConsoleIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  // Local asset (like Stripe / AppsFlyer): the Search Console logo is not in
  // the iconify sets used here, so ship the vendored PNG instead.
  return (
    <img
      src="https://cdn.soku.ai/marketing/integrations/icons/searchconsole.png"
      alt="Google Search Console"
      className={`${sizeClass} object-contain`}
    />
  )
}

export const TikTokIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  return <Icon icon="logos:tiktok-icon" className={sizeClass} />
}

export const LinkedInIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  return <Icon icon="logos:linkedin-icon" className={sizeClass} />
}

export const RedditIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  return <Icon icon="logos:reddit-icon" className={sizeClass} />
}

export const XAdsIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  return <Icon icon="simple-icons:x" className={sizeClass} />
}

export const PostHogIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  return <Icon icon="logos:posthog-icon" className={sizeClass} />
}

export const ShopifyIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  return <Icon icon="logos:shopify" className={sizeClass} />
}

// Shoplazza ships no icon through Iconify (`logos:*`) and has none vendored on
// cdn.soku.ai, so the brand mark is inlined: a vector trace of the official
// 48x48 app mark (https://www.shoplazza.com/hubfs/spark111.png) in their
// primary red. Inline (rather than an <img>) keeps the grid free of a remote
// host at render time, matching MetaIcon / GoogleIcon.
// SHOPLINE's mark: a rounded dark-navy tile with a white "S". Inline SVG for
// the same remote-host-free reason as the siblings; the exact brand art gets
// swapped in with the App Store listing assets.
export const ShoplineIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  return (
    <svg
      className={sizeClass}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="4" y="4" width="40" height="40" rx="9" fill="#0B1F3A" />
      <path
        d="M30.6 16.9c-1.5-1.3-3.6-2-5.9-2-4.3 0-7.4 2.3-7.4 5.7 0 3 2.1 4.4 6 5.3l2 .5c2.4.6 3.4 1.2 3.4 2.5 0 1.5-1.5 2.5-3.9 2.5-2.3 0-4.3-.8-5.8-2.3l-2.2 2.9c1.9 1.8 4.7 2.8 7.9 2.8 4.7 0 7.9-2.3 7.9-5.9 0-3-2-4.5-6.2-5.5l-2-.5c-2.2-.5-3.2-1.2-3.2-2.4 0-1.4 1.4-2.3 3.5-2.3 2 0 3.6.6 5 1.8l2.9-3.1Z"
        fill="#FFFFFF"
      />
    </svg>
  )
}

export const ShoplazzaIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  return (
    <svg
      className={sizeClass}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9 38.75 L11.25 25.88 L16 25.38 L15.75 24.5 L13.25 22.38 L12.38 20 L13 15.88 L14.12 14.12 L18.5 11.25 L22 11 L25.5 12.12 L27.25 14.12 L28.25 14.25 L29.25 10.12 L38.62 8.12 L39 9.5 L36.88 22 L35.5 22.75 L32.12 22.75 L32.25 23.75 L34.88 25.88 L35.62 28.12 L35 32.25 L33.12 34.75 L29.38 36.88 L25.5 37 L22.38 35.88 L20.62 33.88 L19.75 33.88 L18.62 38 L10.38 39.88 L9.25 39.88 Z"
        fill="#FF0000"
      />
    </svg>
  )
}

// Composio-backed integrations render their official toolkit logos, sourced
// from Composio (https://logos.composio.dev/api/<toolkit>) and vendored under
// public/integrations/icons/composio/ so the grid never depends on a remote
// host at render time. `toolkit` matches the Composio toolkit id in
// packages/services/credential_context/oauth/composio_managed_catalog.py.
const ComposioIcon = ({
  toolkit,
  name,
  size = 'md',
}: IconProps & { toolkit: string; name: string }) => {
  const sizeClass = iconSizeMap[size]
  return (
    <img
      src={`https://cdn.soku.ai/marketing/integrations/icons/composio/${toolkit}.svg`}
      alt={name}
      className={`${sizeClass} object-contain`}
    />
  )
}

export const NotionIcon = ({ size = 'md' }: IconProps) => (
  <ComposioIcon toolkit="notion" name="Notion" size={size} />
)

export const GmailIcon = ({ size = 'md' }: IconProps) => (
  <ComposioIcon toolkit="gmail" name="Gmail" size={size} />
)

export const GoogleDocsIcon = ({ size = 'md' }: IconProps) => (
  <ComposioIcon toolkit="googledocs" name="Google Docs" size={size} />
)

export const ChatGPTIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  return <Icon icon="simple-icons:openai" className={sizeClass} />
}

export const StripeIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  return (
    <img
      src="https://cdn.soku.ai/marketing/integrations/icons/stripe.svg"
      alt="Stripe"
      className={`${sizeClass} object-contain`}
    />
  )
}

export const AppsFlyerIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  // Local asset: AppsFlyer was removed from the simple-icons set, so the
  // iconify API returns nothing for it — ship the logo instead (like Stripe).
  return (
    <img
      src="https://cdn.soku.ai/marketing/integrations/icons/appsflyer.svg"
      alt="AppsFlyer"
      className={`${sizeClass} object-contain`}
    />
  )
}

export const AdjustIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  // Local asset: Adjust is not in the simple-icons set — ship the logo.
  return (
    <img
      src="https://cdn.soku.ai/marketing/integrations/icons/adjust.svg"
      alt="Adjust"
      className={`${sizeClass} object-contain`}
    />
  )
}

// AppLovin ships no icon through Iconify (checked: `simple-icons` has no
// `applovin`) and has none vendored on cdn.soku.ai, so the brand mark is
// inlined like Shoplazza / SHOPLINE: a dark tile carrying AppLovin's
// triangular play mark. Inline (rather than an <img>) keeps the grid free of a
// remote host at render time; the official vector gets swapped in whenever the
// marketing asset set is vendored.
export const AppLovinIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  return (
    <svg
      className={sizeClass}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="4" y="4" width="40" height="40" rx="9" fill="#12131A" />
      <path
        d="M24 13.5 L34.5 33 L27.6 33 L24 25.6 L20.4 33 L13.5 33 Z"
        fill="#FFFFFF"
      />
    </svg>
  )
}

export const CloudXIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  // Inline tile: no Iconify entry and nothing vendored on cdn.soku.ai; a
  // neutral "CX" monogram, not the vendor logo.
  return (
    <svg
      className={sizeClass}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="4" y="4" width="40" height="40" rx="9" fill="#0F172A" />
      <text
        x="24"
        y="30"
        textAnchor="middle"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontSize="17"
        fontWeight="700"
        fill="#FFFFFF"
      >
        CX
      </text>
    </svg>
  )
}

export const SensorTowerIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  // Inline tile: neutral "ST" monogram, not the vendor logo.
  return (
    <svg
      className={sizeClass}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="4" y="4" width="40" height="40" rx="9" fill="#0B7A75" />
      <text
        x="24"
        y="30"
        textAnchor="middle"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontSize="17"
        fontWeight="700"
        fill="#FFFFFF"
      >
        ST
      </text>
    </svg>
  )
}

export const ThinkingDataIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  // Inline tile: ThinkingData ships no Iconify entry and nothing is vendored
  // on cdn.soku.ai; the mark is a neutral "TD" monogram, not the vendor logo.
  return (
    <svg
      className={sizeClass}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="4" y="4" width="40" height="40" rx="9" fill="#1F4FD8" />
      <text
        x="24"
        y="30"
        textAnchor="middle"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontSize="17"
        fontWeight="700"
        fill="#FFFFFF"
      >
        TD
      </text>
    </svg>
  )
}

export const ToastIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  // Local asset: Toast ships no icon through Iconify — the official brand
  // mark is vendored on cdn.soku.ai, like Stripe / AppsFlyer / Adjust /
  // Sprout Social.
  return (
    <img
      src="https://cdn.soku.ai/marketing/integrations/icons/toast.svg"
      alt="Toast"
      className={`${sizeClass} object-contain`}
    />
  )
}

export const DoorDashIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  // DoorDash ships through Iconify's simple-icons set, so no vendored CDN
  // asset is needed (same sourcing as the ChatGPT card's simple-icons:openai).
  // simple-icons strips brand palettes to currentColor, so the official
  // DoorDash red is reapplied locally to keep the grid's marks on-brand.
  return (
    <Icon
      icon="simple-icons:doordash"
      className={`${sizeClass} text-[#FF3008]`}
    />
  )
}

// Uber Eats ships no icon through Iconify (`logos:*`) and has none vendored on
// cdn.soku.ai, so the mark is inlined: a rounded tile in Uber Eats' brand
// green (#06C167) carrying a white utensils glyph (lucide's `utensils` paths).
// Inline (rather than an <img>) keeps the grid free of a remote host at render
// time, matching MetaIcon / GoogleIcon / ShoplazzaIcon.
export const UberEatsIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  return (
    <svg
      className={sizeClass}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="2" y="2" width="44" height="44" rx="10" fill="#06C167" />
      <g
        transform="translate(12 12)"
        stroke="#FFFFFF"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
        <path d="M7 2v20" />
        <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
      </g>
    </svg>
  )
}

export const SproutSocialIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  // Local asset: Sprout Social ships no icon through Iconify — the official
  // brand mark (vectors from their favicon SVG) is vendored on cdn.soku.ai,
  // like Stripe / AppsFlyer / Adjust.
  return (
    <img
      src="https://cdn.soku.ai/marketing/integrations/icons/sprout-social.svg"
      alt="Sprout Social"
      className={`${sizeClass} object-contain`}
    />
  )
}

export const GoogleSheetsIcon = ({ size = 'md' }: IconProps) => (
  <ComposioIcon toolkit="googlesheets" name="Google Sheets" size={size} />
)

export const GoogleDriveIcon = ({ size = 'md' }: IconProps) => (
  <ComposioIcon toolkit="googledrive" name="Google Drive" size={size} />
)

export const GoogleCalendarIcon = ({ size = 'md' }: IconProps) => (
  <ComposioIcon toolkit="googlecalendar" name="Google Calendar" size={size} />
)

export const FirefliesIcon = ({ size = 'md' }: IconProps) => (
  <ComposioIcon toolkit="fireflies" name="Fireflies" size={size} />
)

export const MondayIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  return <Icon icon="logos:monday-icon" className={sizeClass} />
}

export const BigQueryIcon = ({ size = 'md' }: IconProps) => {
  const sizeClass = iconSizeMap[size]
  // `gcp:*` is Google Cloud's own Apache-2.0 product icon set on Iconify.
  // BigQuery is absent from `logos:` (only `logos:google-cloud` exists) and
  // has no vendored Composio asset on cdn.soku.ai, so this is the one source
  // that renders the actual product mark rather than a generic cloud glyph.
  return <Icon icon="gcp:bigquery" className={sizeClass} />
}

export const getIntegrations = (
  iconSize: 'sm' | 'md' | 'lg' = 'md',
  overrides: IntegrationRuntimeOverrides = {},
): Integration[] => [
  {
    id: 'chatgpt',
    section: 'channels',
    name: 'ChatGPT Ads',
    icon: <ChatGPTIcon size={iconSize} />,
    description:
      'Install the Soku Pixel and view ChatGPT Ads report accounts provisioned by Soku support',
    supportsOAuth: false,
    enabled: isIntegrationEnabled('chatgpt', overrides),
    onboarding: { section: 'ads', rank: 1 },
    badge: 'new',
  },
  {
    id: 'meta',
    section: 'channels',
    name: 'Meta Ads',
    icon: <MetaIcon size={iconSize} />,
    description:
      'Connect your Meta ads account to get insights and optimize campaigns',
    supportsOAuth: true,
    enabled: isIntegrationEnabled('meta', overrides),
    onboarding: { section: 'ads', rank: 2 },
  },
  {
    id: 'google',
    section: 'channels',
    name: 'Google Ads',
    icon: <GoogleIcon size={iconSize} />,
    description:
      'Connect your Google ads account to get insights and optimize campaigns',
    supportsOAuth: true,
    enabled: isIntegrationEnabled('google', overrides),
    onboarding: { section: 'ads', rank: 3 },
  },
  {
    id: 'admob',
    section: 'revenue',
    name: 'Google AdMob',
    icon: <Icon icon="logos:google-admob" className={iconSizeMap[iconSize]} />,
    description:
      'Read app monetization, network and mediation earnings with Google AdMob',
    supportsOAuth: true,
    enabled: isIntegrationEnabled('admob', overrides),
  },
  {
    id: 'ga4',
    section: 'analytics',
    name: 'GA4',
    icon: <GA4Icon size={iconSize} />,
    description:
      'Connect your Google Analytics to track conversions and user behavior',
    supportsOAuth: true,
    enabled: isIntegrationEnabled('ga4', overrides),
    onboarding: { section: 'ads', rank: 5 },
  },
  {
    id: 'gtm',
    section: 'analytics',
    name: 'Google Tag Manager',
    icon: (
      <Icon icon="logos:google-tag-manager" className={iconSizeMap[iconSize]} />
    ),
    description:
      'Manage tags, triggers and container versions with approval before publishing',
    supportsOAuth: true,
    enabled: isIntegrationEnabled('gtm', overrides),
  },
  {
    id: 'gsc',
    section: 'analytics',
    name: 'Google Search Console',
    icon: <SearchConsoleIcon size={iconSize} />,
    description:
      'Connect Google Search Console to analyze organic queries, pages, and site performance',
    supportsOAuth: true,
    enabled: isIntegrationEnabled('gsc', overrides),
    onboarding: { section: 'data', rank: 1 },
  },
  {
    id: 'posthog',
    section: 'analytics',
    name: 'PostHog',
    icon: <PostHogIcon size={iconSize} />,
    description:
      'Connect PostHog to analyze funnels, retention, and product events',
    supportsOAuth: true,
    enabled: isIntegrationEnabled('posthog', overrides),
    onboarding: { section: 'data', rank: 2 },
  },
  {
    id: 'tiktok',
    section: 'channels',
    name: 'TikTok Ads',
    icon: <TikTokIcon size={iconSize} />,
    description:
      'Connect your TikTok ads account to get insights and optimize campaigns',
    supportsOAuth: true,
    enabled: isIntegrationEnabled('tiktok', overrides),
    onboarding: { section: 'ads', rank: 4 },
  },
  {
    id: 'linkedin',
    section: 'channels',
    name: 'LinkedIn Ads',
    icon: <LinkedInIcon size={iconSize} />,
    description:
      'Connect your LinkedIn ad accounts to get insights and manage B2B campaigns',
    supportsOAuth: true,
    enabled: isIntegrationEnabled('linkedin', overrides),
    onboarding: { section: 'ads', rank: 6 },
  },
  {
    id: 'reddit',
    section: 'channels',
    name: 'Reddit Ads',
    icon: <RedditIcon size={iconSize} />,
    description:
      'Connect your Reddit ad accounts to review performance and manage campaigns',
    supportsOAuth: true,
    enabled: isIntegrationEnabled('reddit', overrides),
  },
  {
    id: 'x_ads',
    section: 'channels',
    name: 'X Ads',
    icon: <XAdsIcon size={iconSize} />,
    description:
      'Connect your X ad accounts to review performance and manage campaigns',
    supportsOAuth: true,
    enabled: isIntegrationEnabled('x_ads', overrides),
  },
  {
    id: 'shopify',
    section: 'revenue',
    name: 'Shopify',
    icon: <ShopifyIcon size={iconSize} />,
    description:
      'Connect your Shopify store to read products, orders, and customers',
    supportsOAuth: true,
    enabled: isIntegrationEnabled('shopify', overrides),
  },
  {
    id: 'shoplazza',
    section: 'revenue',
    name: 'Shoplazza',
    icon: <ShoplazzaIcon size={iconSize} />,
    description:
      'Link the Shoplazza stores installed from the Shoplazza App Store to your brands',
    // The store's OAuth token is parked when the merchant installs the app;
    // Soku never starts an authorize hop of its own.
    supportsOAuth: false,
    enabled: isIntegrationEnabled('shoplazza', overrides),
  },
  {
    id: 'shopline',
    section: 'revenue',
    name: 'SHOPLINE',
    icon: <ShoplineIcon size={iconSize} />,
    description:
      'Link the SHOPLINE stores installed from the SHOPLINE App Store to your brands',
    // Same posture as Shoplazza: the store's OAuth token is parked when the
    // merchant installs the app; Soku never starts an authorize hop.
    supportsOAuth: false,
    enabled: isIntegrationEnabled('shopline', overrides),
  },
  {
    id: 'stripe',
    section: 'revenue',
    name: 'Stripe',
    icon: <StripeIcon size={iconSize} />,
    description:
      'Connect Stripe to look up payments, customers, and subscriptions',
    supportsOAuth: true,
    enabled: platformEnabled.stripe,
  },
  {
    id: 'appsflyer',
    section: 'analytics',
    name: 'AppsFlyer',
    icon: <AppsFlyerIcon size={iconSize} />,
    description:
      'Connect AppsFlyer to read mobile attribution, ROAS, SKAN, and audiences',
    supportsOAuth: true,
    enabled: platformEnabled.appsflyer,
  },
  {
    id: 'adjust',
    section: 'analytics',
    name: 'Adjust',
    icon: <AdjustIcon size={iconSize} />,
    description:
      'Connect Adjust to read mobile attribution, ROAS, SKAN, and retention',
    supportsOAuth: true,
    enabled: platformEnabled.adjust,
  },
  {
    id: 'topon',
    section: 'revenue',
    name: 'TopOn',
    icon: (
      <span aria-hidden="true" className="font-semibold">
        T
      </span>
    ),
    description:
      'Read your App monetization revenue, hourly estimates, LTV and retention',
    supportsOAuth: false,
    enabled: platformEnabled.topon,
  },
  {
    id: 'cloudx',
    section: 'revenue',
    name: 'CloudX',
    icon: <CloudXIcon size={iconSize} />,
    description:
      'Read CloudX mediation reports and auctions; edit inventory configuration after approval',
    // Paste-key only: CloudX API keys are created by an account administrator
    // under Settings > API Keys; the hosted MCP is OAuth-session only.
    supportsOAuth: false,
    enabled: platformEnabled.cloudx,
  },
  {
    id: 'sprout',
    section: 'analytics',
    name: 'Sprout Social',
    icon: <SproutSocialIcon size={iconSize} />,
    description:
      'Connect Sprout Social to read social profile and post analytics',
    // Paste-token only: Sprout's Public API issues personal API tokens, no
    // OAuth flow (like Fireflies' API-key connect).
    supportsOAuth: false,
    enabled: platformEnabled.sprout,
  },
  {
    id: 'applovin',
    section: 'channels',
    name: 'AppLovin Ads',
    icon: <AppLovinIcon size={iconSize} />,
    description:
      'Read-only AppLovin campaign, creative and asset performance, spend, cohort ROAS and retention',
    // Paste-key only: AppLovin issues a static Report Key from the dashboard
    // and has no third-party OAuth flow of any kind.
    supportsOAuth: false,
    enabled: platformEnabled.applovin,
  },
  {
    id: 'applovin_max',
    section: 'revenue',
    name: 'AppLovin MAX',
    icon: <AppLovinIcon size={iconSize} />,
    description:
      'Read-only MAX estimated revenue, impressions, eCPM and network fill rate',
    // Paste-key only: AppLovin issues a static Report Key from the dashboard
    // and has no third-party OAuth flow of any kind.
    supportsOAuth: false,
    enabled: platformEnabled.applovin_max,
  },
  {
    id: 'thinkingdata',
    section: 'analytics',
    name: 'ThinkingData',
    icon: <ThinkingDataIcon size={iconSize} />,
    description:
      'Read-only ThinkingData (TA/TE) events, properties, product metrics and human-reviewed SQL',
    // Paste-credentials only: the project key is generated on the customer's
    // own TA/TE server with ta-tool; there is no OAuth flow of any kind.
    supportsOAuth: false,
    enabled: platformEnabled.thinkingdata,
  },
  {
    id: 'sensortower',
    section: 'analytics',
    name: 'Sensor Tower',
    icon: <SensorTowerIcon size={iconSize} />,
    description:
      'Read-only Sensor Tower market intelligence: download, revenue, active-user and retention estimates, top charts and ad creatives',
    // Paste-token only: the API token comes from the customer's own Sensor
    // Tower account page; there is no OAuth flow.
    supportsOAuth: false,
    enabled: platformEnabled.sensortower,
  },
  {
    id: 'toast',
    section: 'revenue',
    name: 'Toast',
    icon: <ToastIcon size={iconSize} />,
    description:
      'Connect Toast to read restaurant orders, checks, payments and refunds',
    // Paste-credentials only: Toast Standard API access issues a client ID /
    // secret pair created in Toast Web, with no third-party OAuth flow.
    supportsOAuth: false,
    enabled: platformEnabled.toast,
  },
  {
    id: 'doordash',
    section: 'revenue',
    name: 'DoorDash',
    icon: <DoorDashIcon size={iconSize} />,
    description:
      'Read-only orders, sales, payouts & ads performance from authorized DoorDash stores',
    // Paste-nothing connect: DoorDash reporting runs on the Soku platform
    // credential, so the customer only claims the business IDs their DoorDash
    // Account Owner authorized. No OAuth flow exists.
    supportsOAuth: false,
    enabled: platformEnabled.doordash,
  },
  {
    id: 'ubereats',
    section: 'revenue',
    name: 'Uber Eats',
    icon: <UberEatsIcon size={iconSize} />,
    description:
      'Connect Uber Eats and choose which stores to authorize — read-only store info, orders, sales, payments and financial reports',
    // OAuth-only: merchant tokens come exclusively from Uber's hosted consent
    // flow; there is no pasted-token alternative.
    supportsOAuth: true,
    enabled: isIntegrationEnabled('ubereats', overrides),
  },
  {
    id: 'bigquery',
    section: 'analytics',
    name: 'BigQuery',
    icon: <BigQueryIcon size={iconSize} />,
    description:
      'Connect BigQuery to explore datasets and run reviewed SQL queries',
    // Two connect modes under one card: a pasted service-account key
    // (platform 'bigquery') and Composio managed OAuth (toolkit
    // 'googlebigquery'). `supportsOAuth` marks the second one.
    supportsOAuth: true,
    enabled: isIntegrationEnabled('bigquery', overrides),
  },
  {
    id: 'notion',
    section: 'docs',
    name: 'Notion',
    icon: <NotionIcon size={iconSize} />,
    description:
      'Connect your Notion workspace to create pages and query databases',
    supportsOAuth: true,
    enabled: platformEnabled.notion,
    onboarding: { section: 'other', rank: 1 },
  },
  {
    id: 'gmail',
    section: 'comms',
    name: 'Gmail',
    icon: <GmailIcon size={iconSize} />,
    description: 'Connect Gmail to read and send messages from your account',
    supportsOAuth: true,
    enabled: platformEnabled.gmail,
    onboarding: { section: 'other', rank: 2 },
  },
  {
    id: 'googledocs',
    section: 'docs',
    name: 'Google Docs',
    icon: <GoogleDocsIcon size={iconSize} />,
    description:
      'Connect Google Docs to create and read documents in your Drive',
    supportsOAuth: true,
    enabled: platformEnabled.googledocs,
    onboarding: { section: 'other', rank: 3 },
  },
  {
    id: 'googlesheets',
    section: 'docs',
    name: 'Google Sheets',
    icon: <GoogleSheetsIcon size={iconSize} />,
    description:
      'Connect Google Sheets to read and write spreadsheets in your Drive',
    supportsOAuth: true,
    enabled: platformEnabled.googlesheets,
    onboarding: { section: 'other', rank: 4 },
  },
  {
    id: 'googledrive',
    section: 'docs',
    name: 'Google Drive',
    icon: <GoogleDriveIcon size={iconSize} />,
    description:
      'Connect Google Drive to find, read, and manage files in your Drive',
    supportsOAuth: true,
    enabled: platformEnabled.googledrive,
    onboarding: { section: 'other', rank: 5 },
  },
  {
    id: 'googlecalendar',
    section: 'work',
    name: 'Google Calendar',
    icon: <GoogleCalendarIcon size={iconSize} />,
    description:
      'Connect Google Calendar to read your schedule and create or update events',
    supportsOAuth: true,
    enabled: platformEnabled.googlecalendar,
    onboarding: { section: 'other', rank: 6 },
  },
  {
    id: 'fireflies',
    section: 'ai',
    name: 'Fireflies',
    icon: <FirefliesIcon size={iconSize} />,
    description:
      'Connect Fireflies to read your meeting transcripts and summaries',
    // API-key toolkit: connects via the API-key dialog, not the OAuth popup.
    supportsOAuth: false,
    enabled: platformEnabled.fireflies,
  },
  {
    id: 'monday',
    section: 'work',
    name: 'Monday.com',
    icon: <MondayIcon size={iconSize} />,
    description:
      'Connect Monday.com to read boards and items and create or update them',
    supportsOAuth: true,
    enabled: platformEnabled.monday,
  },
  {
    id: 'chatbot',
    section: 'channels',
    name: 'Chatbot Ads',
    icon: <BotIcon className={iconSizeMap[iconSize]} />,
    description:
      'Connect your Chatbot ads account to get insights and optimize campaigns',
    supportsOAuth: false,
    enabled: platformEnabled.chatbot,
  },
]

/**
 * The card icon for a platform id, or null when no card owns that id.
 *
 * Reads the registry above rather than carrying a second platform -> icon map:
 * a hand-maintained copy would silently keep rendering the old mark after a
 * card swaps its icon. Callers that need an icon outside a card (the connect
 * dialog's OAuth panel) go through here.
 */
export function integrationIcon(
  platform: IntegrationPlatform | string,
  size: 'sm' | 'md' | 'lg' = 'md',
): React.ReactNode {
  return getIntegrations(size).find(item => item.id === platform)?.icon ?? null
}

/**
 * Resolve card copy to the active locale.
 *
 * `name` is a brand name and stays verbatim; only `description` is localized,
 * from `SettingsPages.integrations.catalog.descriptions.<id>`. The English
 * literal in the registry above remains the fallback, so an integration added
 * without its message keys still renders (in English) instead of throwing.
 *
 * Takes an already-built list rather than calling `getIntegrations` itself:
 * that call allocates fresh icon elements every time, so callers memoize it
 * and depend on the stable identity.
 */
export const useLocalizedIntegrations = (
  integrations: Integration[],
): Integration[] => {
  const t = useTranslations('SettingsPages.integrations.catalog.descriptions')
  return useMemo(
    () =>
      integrations.map(integration => {
        // Ids are a closed union, but next-intl types keys off the base
        // messages file — cast and guard with the runtime `has`.
        const key = integration.id as Parameters<typeof t>[0]
        return t.has(key)
          ? { ...integration, description: t(key) }
          : integration
      }),
    [integrations, t],
  )
}
